import type { ReactNode } from 'react';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { ConfirmAction } from '@eis/patterns';

/**
 * "Activate this version" — the confirmation (prompt 14.5, SDD-016 D-V3).
 *
 * Activating moves the generation's pointer, which decides what every NEW policy pins. The dialog
 * says the one thing an operator fears and the platform guarantees: nothing already in force
 * moves. It is controlled — the trigger is an entry of the "Status ändern" menu
 * (`VersionStatusActions`, prompt 24), which closes before the dialog opens.
 */
export function ActivateVersionDialog({
  version,
  open,
  busy,
  onCancel,
  onActivate,
}: {
  /** The version name as the operator reads it — `1.2`. */
  readonly version: string;
  readonly open: boolean;
  readonly busy: boolean;
  readonly onCancel: () => void;
  readonly onActivate: () => void;
}): ReactNode {
  const { t } = useTranslation('insurance');
  return (
    <ConfirmAction
      open={open}
      title={t('authoring.activate.title', { version })}
      description={t('authoring.activate.description', { version })}
      confirmLabel={t('authoring.activate.confirm')}
      busy={busy}
      onCancel={onCancel}
      onConfirm={onActivate}
    >
      <Stack spacing={1}>
        {/* The reassurance, stated as a fact about the platform rather than as advice. */}
        <Alert severity="info">{t('authoring.activate.noPolicyMoves')}</Alert>
        <Typography variant="body2" color="text.secondary">
          {t('authoring.activate.appliesToNew')}
        </Typography>
      </Stack>
    </ConfirmAction>
  );
}
